import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'chart.js/auto';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { useLocale, fmtNumber } from '../i18n/format';
import ExportButton from '../components/ExportButton';
import { buildAhaDoc } from '../pdf/builders/aha/buildDoc';
import { buildAhaWorkbook } from '../excel/builders/aha/buildWorkbook';

// --- helpers ---
const WEEKDAY_ISO_TO_LABEL = (t) => ({
  1: t('aha.weekdays.mon', 'Mon'),
  2: t('aha.weekdays.tue', 'Tue'),
  3: t('aha.weekdays.wed', 'Wed'),
  4: t('aha.weekdays.thu', 'Thu'),
  5: t('aha.weekdays.fri', 'Fri'),
  6: t('aha.weekdays.sat', 'Sat'),
  7: t('aha.weekdays.sun', 'Sun'),
});

function clamp(n, a, b){ return Math.max(a, Math.min(b, n)); }
function safeNum(v){ const n = Number(v); return Number.isFinite(n) ? n : 0; }

// color scale: 0..1 -> HSL (226..216 hue, 14..52% lightness)
function valueToColor01(x){
  const hue = 226 - 10*x;         // синевато-фиолетовый -> более насыщенный
  const light = 52 - 38*x;        // ярче -> темнее
  const sat  = 95 - 10*(1-x);     // почти насыщенный
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

export default function AhaServicePage(){
  const { t, i18n } = useTranslation('common');
  const { isAuth } = useAuth();
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const locale = useLocale();
  const dateInputLang = (locale || i18n.language || 'uk');
  const langCode = (dateInputLang || 'uk').split('-')[0];
  // переключатель вида: 'heatmap' | 'lines'
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('aha.viewMode') || 'heatmap'; } catch { return 'heatmap'; }
  });
  useEffect(() => {
    try { localStorage.setItem('aha.viewMode', viewMode); } catch {}
  }, [viewMode]);
  const lineChartRef = useRef(null);
  const lineChartInst = useRef(null);

  // --- controls state ---
  // Кінцева дата періоду + глибина (1,3,6,9,12 місяців)
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  });
  const [monthsDepth, setMonthsDepth] = useState(1); // 1|3|6|9|12
  // Метрика фиксирована: используем единственные реальные данные из БД (ботов нет).

  // --- data state ---
  const [matrix, setMatrix] = useState([]); // [7][24]
  const [hours, setHours]   = useState([]); // [0..23]
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [meta, setMeta]       = useState(null);

  // CSV download helper
  const handleExportCSV = () => {
    if (!meta || !Array.isArray(hours) || !Array.isArray(matrix)) return;

    const wdNames = {
      uk: ['Пн','Вт','Ср','Чт','Пт','Сб','Нд'],
      ru: ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'],
      en: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'],
      it: ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'],
    };
    const dn = wdNames[langCode] || wdNames.uk;

    const header = ['Day/Hour', ...hours.map(h => String(h).padStart(2, '0'))];
    const rows = [header];

    for (let i = 0; i < 7; i++) {
      const label = dn[i] || `D${i + 1}`;
      const vals = (matrix[i] || []).map(v => (Number.isFinite(v) ? v : 0));
      rows.push([label, ...vals]);
    }

    const metaLines = [
      `# Channel: ${meta?.channel || ''}`,
      `# Period end: ${meta?.period?.end_date || ''}`,
      `# Months: ${meta?.period?.months ?? ''}`,
      `# TZ: ${meta?.tz || 'Europe/Kyiv'}`,
      `# Method: mean(points/min) -> mean(min/hour) -> mean by weekday/hour`,
      `# Generated: ${new Date().toISOString()}`,
      `#`,
    ];

    const csvBody = rows
      .map(r => r.map(cell => {
        const s = String(cell ?? '');
        return (s.includes(';') || s.includes('"') || s.includes('\n'))
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      }).join(';'))
      .join('\n');

    const csv = metaLines.join('\n') + '\n' + csvBody + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const end = meta?.period?.end_date || 'period';
    a.href = url;
    a.download = `aha_${meta?.channel || 'channel'}_${end}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // нормализация даты (приходит как YYYY-MM-DD из <input type="date">,
  // но на некоторых системах может пролететь "17.10.2025" / "17-10-2025")
  function toYYYYMMDD(s){
    if (!s) return s;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // уже норм
    const m = String(s).match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);
    if (m) {
      const dd = m[1].padStart(2,'0');
      const mm = m[2].padStart(2,'0');
      const yy = m[3];
      return `${yy}-${mm}-${dd}`;
    }
    return s; // оставляем как есть (посмотрим в консоли)
  }

  // fetch AHA
  const handleFetch = async (e) => {
    if (e) e.preventDefault();
    if (!isAuth) return;
    if (!radioId){
      setError(t('common.no_channel'));
      return;
    }
    setLoading(true);
    setError('');
    setMatrix([]);
    setHours([]);
    setMeta(null);

    try{
      const endISO = toYYYYMMDD(endDate);
      const params = new URLSearchParams({
        end_date: endISO,
        months: String(monthsDepth),
      });
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/aha?${params.toString()}`;
      const DEBUG_URL = url;
      // eslint-disable-next-line no-console
      console.log('[AHA request]', DEBUG_URL);
      const resp = await authFetch(url);
      let json;
      if (!resp.ok) {
        try { json = await resp.json(); } catch (_) { /* ignore */ }
        const backendMsg = json?.detail || json?.error || json?.message || '';
        throw new Error(`${t('aha.errors.http', { status: resp.status })}${backendMsg ? `: ${backendMsg}` : ''}`);
      }
      json = json || (await resp.json());
      if (json?.error || json?.detail) {
        throw new Error(String(json.error || json.detail));
      }
      if (!json || !Array.isArray(json.matrix) || !Array.isArray(json.hours)){
        throw new Error(t('aha.errors.no_data'));
      }
      setMatrix(json.matrix);
      setHours(json.hours);
      setMeta(json.meta || null);
    }catch(err){
      setError(err?.message || t('aha.errors.generic'));
    }finally{
      setLoading(false);
    }
  };

  const wdLabels = useMemo(() => WEEKDAY_ISO_TO_LABEL(t), [t]);

  useEffect(() => {
    // авто-загрузка при первом появлении канала
    if (radioId && isAuth) handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioId]);

  // побудова лінійного графіка (X = години 0..23, Y = значення для кожного дня тижня окремою лінією)
  useEffect(() => {
    if (viewMode !== 'lines') {
      if (lineChartInst.current) { lineChartInst.current.destroy(); lineChartInst.current = null; }
      return;
    }
    const hoursArr = Array.isArray(hours) && hours.length ? hours : Array.from({ length: 24 }, (_, i) => i);
    const dataMatrix = Array.isArray(matrix) ? matrix : [];

    if (!lineChartRef.current || !dataMatrix.length) {
      return;
    }
    if (lineChartInst.current) {
      lineChartInst.current.destroy();
      lineChartInst.current = null;
    }

    const weekdayLabels = dataMatrix.map((_, idx) => wdLabels[idx + 1] || `D${idx + 1}`);
    const datasets = dataMatrix.map((row, idx) => ({
      label: weekdayLabels[idx],
      data: (row || []).map(v => Number.isFinite(v) ? v : 0),
      fill: false,
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBorderWidth: 0,
      tension: 0.25,
    }));

    const ctx = lineChartRef.current.getContext('2d');
    lineChartInst.current = new Chart(ctx, {
      type: 'line',
      data: { labels: hoursArr.map(h => String(h).padStart(2,'0')), datasets },
      options: {
        responsive: true,
        plugins: {
          legend: { position: 'right' },
          datalabels: { display: false }
        },
        scales: {
          x: { title: { display: true, text: t('aha.axis.hour', 'Година доби') } },
          y: { title: { display: true, text: t('aha.axis.audience', 'Середня аудиторія') }, beginAtZero: true, ticks: { precision: 0 } }
        }
      }
    });

    return () => {
      if (lineChartInst.current) {
        lineChartInst.current.destroy();
        lineChartInst.current = null;
      }
    };
  }, [viewMode, matrix, hours, wdLabels, t]);

  // вычислим min/max для цветовой шкалы
  const { minVal, maxVal, avgVal } = useMemo(() => {
    const flat = matrix.flat().map(safeNum);
    const nums = flat.filter(n => n > 0);
    if (!nums.length) return {minVal:0, maxVal:0, avgVal:0};
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const avg = nums.reduce((a,b)=>a+b,0)/nums.length;
    return {minVal:min, maxVal:max, avgVal:avg};
  }, [matrix]);

  // Цвет ячейки с учётом min/max
  const cellColor = (v) => {
    if (!maxVal || v<=0) return '#f1f3f8';
    const x = clamp((v - minVal) / (maxVal - minVal || 1), 0, 1);
    return valueToColor01(x);
  };

  const exportDisabled =
    loading ||
    !radioId ||
    !Array.isArray(matrix) ||
    !matrix.length ||
    !Array.isArray(hours) ||
    !hours.length;

  const exportButtonStyle = {
    ...btn(false),
    minWidth: 110,
    padding: '9px 14px',
    background: exportDisabled ? '#a8b7ff' : '#4b64ff',
    cursor: exportDisabled ? 'not-allowed' : 'pointer',
    opacity: exportDisabled ? 0.85 : 1
  };

  const filenameSuffixRaw = meta?.period?.end_date || endDate || 'period';
  const filenameSuffix = String(filenameSuffixRaw || 'period').replace(/[^0-9A-Za-z_-]/g, '-');
  const exportFilenameBase = `aha_${(shortcode || 'channel').replace(/[^0-9A-Za-z_-]/g, '-')}_${filenameSuffix}`;

  return (
    <div style={wrap}>
      <div style={lead}>{t('aha.lead', { station: stationName || '' })}</div>

      <h2 style={title}>{t('aha.title')}</h2>

      <form onSubmit={handleFetch} style={controls}>
        <div style={row}>
          <label style={fieldDate} htmlFor="aha-end-date">
            <span style={fieldLabelDate}>📅 {t('aha.end_date')}</span>
            <input
              id="aha-end-date"
              type="date"
              value={endDate}
              onChange={(e)=>setEndDate(e.target.value)}
              style={input}
              lang={dateInputLang}
            />
          </label>

          <label style={fieldDepth} htmlFor="aha-months-depth">
            <span style={fieldLabelDepth}>📈 {t('aha.depth')}</span>
            <select
              id="aha-months-depth"
              value={monthsDepth}
              onChange={(e)=>setMonthsDepth(Number(e.target.value))}
              style={select}
            >
              <option value={1}>{t('aha.depth_1m')}</option>
              <option value={3}>{t('aha.depth_3m')}</option>
              <option value={6}>{t('aha.depth_6m')}</option>
              <option value={9}>{t('aha.depth_9m')}</option>
              <option value={12}>{t('aha.depth_12m')}</option>
            </select>
          </label>

          <div style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="submit" disabled={loading || !radioId} style={btn(loading)}>
              {loading ? t('common.loading') : t('common.show')}
            </button>
            <ExportButton
              data={{ matrix, hours, meta }}
              onBeforeBuild={async () => {
                if (viewMode === 'lines' && lineChartRef.current && lineChartRef.current.toDataURL) {
                  try {
                    return { chartDataUrl: lineChartRef.current.toDataURL('image/png') };
                  } catch {
                    return {};
                  }
                }
                return {};
              }}
              buildDoc={(payload) =>
                buildAhaDoc({
                  t,
                  shortcode,
                  stationName,
                  serviceName: t('aha.title'),
                  matrix,
                  hours,
                  meta,
                  fmtNumber,
                  locale,
                  viewMode,
                  chartDataUrl: payload?.chartDataUrl || null
                })
              }
              buildWorkbook={(XLSX) =>
                buildAhaWorkbook(XLSX, {
                  t,
                  shortcode,
                  stationName,
                  serviceName: t('aha.title'),
                  matrix,
                  hours,
                  meta
                })
              }
              filename={`${exportFilenameBase}.pdf`}
              xlsxFilename={`${exportFilenameBase}.xlsx`}
              label={t('export_pdf')}
              labelXlsx={t('export_excel')}
              style={exportButtonStyle}
              disabled={exportDisabled}
            />
          </div>
        </div>
        <div style={{ display:'flex', gap:8, marginTop:10 }}>
          <button
            type="button"
            onClick={() => setViewMode('heatmap')}
            style={{
              background: viewMode==='heatmap' ? '#3867fa' : '#f4f4fa',
              color: viewMode==='heatmap' ? '#fff' : '#222',
              border: 'none', padding: '8px 14px', borderRadius: 6, fontWeight: 600, cursor: 'pointer'
            }}
            aria-pressed={viewMode==='heatmap'}
          >
            {t('aha.view.heatmap', 'Теплокарта')}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('lines')}
            style={{
              background: viewMode==='lines' ? '#3867fa' : '#f4f4fa',
              color: viewMode==='lines' ? '#fff' : '#222',
              border: 'none', padding: '8px 14px', borderRadius: 6, fontWeight: 600, cursor: 'pointer'
            }}
            aria-pressed={viewMode==='lines'}
          >
            {t('aha.view.lines', 'Лінійний графік')}
          </button>
          <button
            type="button"
            onClick={handleExportCSV}
            style={{
              background:'#0f9d58', color:'#fff', border:'none',
              padding:'8px 14px', borderRadius:6, fontWeight:600,
              cursor:'pointer', visibility:'hidden', opacity:0
            }}
            disabled={!meta}
          >
            Експорт CSV
          </button>
        </div>
      </form>

      {/* Підсумок періоду та легенда метрики */}
      <div style={{
        width:'100%', margin:'8px 0 12px', background:'#f8f9ff',
        border:'1px solid #e1e6ff', borderRadius:10, padding:'10px 12px',
        display:'grid', gridTemplateColumns:'1fr', gap:6, boxSizing:'border-box'
      }}>
        {meta?.period && (
          <div style={{display:'flex', flexWrap:'wrap', gap:14, alignItems:'center'}}>
            <div style={{fontWeight:700}}>
              {t('aha.end_date')}
              <span style={{marginLeft:6, fontWeight:800}}>
                {meta?.period?.end_date || '—'}
              </span>
            </div>
            <div style={{opacity:.85}}>
              {t('aha.depth')}
              <span style={{marginLeft:6, fontWeight:700}}>
                {t('aha.months_label', { count: meta?.period?.months ?? monthsDepth })}
              </span>
            </div>
            <div style={{opacity:.6}}>
              {t('aha.timezone', 'Часовий пояс')}: {meta?.tz || 'Europe/Kyiv'}
            </div>
          </div>
        )}

      </div>

      {error && <div style={errorBox}>{error}</div>}

      {/* Heatmap */}
      <div style={{ display: viewMode==='heatmap' ? 'block' : 'none' }}>
        <div style={card}>
          <div style={legendRow}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={legendLabel}>{t('aha.legend.min')}</span>
              <div style={gradientBar}/>
              <span style={legendLabel}>{t('aha.legend.max')}</span>
            </div>
            <div style={{fontSize:13,color:'#666'}}>
              {t('aha.legend.avg')}: <b>{fmtNumber(avgVal, locale)}</b>
            </div>
          </div>

          <div style={gridWrap}>
            {/* Первая строка: заголовки часов */}
            <div style={gridHeaderCell}/>
            {hours.map((h) => (
              <div key={`h${h}`} style={gridHour}>
                {String(h).padStart(2,'0')}
              </div>
            ))}

            {/* 7 строк будней (ISO: 1..7) */}
            {Array.from({length:7}, (_,i)=>i+1).map((wd) => (
              <React.Fragment key={`wd${wd}`}>
                <div style={gridWday}>{wdLabels[wd]}</div>
                {hours.map((h) => {
                  const v = safeNum(matrix?.[wd-1]?.[h]);
                  return (
                    <div key={`c${wd}-${h}`} style={{
                      ...cell,
                      background: cellColor(v),
                      color: v ? 'white' : '#888',
                    }} title={`${wdLabels[wd]} ${String(h).padStart(2,'0')}:00 — ${fmtNumber(v, locale)}`}>
                      {fmtNumber(v, locale)}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* канвас для лінійного графіка */}
      <div style={{ display: viewMode==='lines' ? 'block' : 'none' }}>
        <canvas ref={lineChartRef} width={900} height={360} style={{ width:'100%', height:360, border:'1px solid #ddd', borderRadius:10, background:'#fafafa' }} />
      </div>
    </div>
  );
}

// --- styles ---
const wrap = { maxWidth: 1200, margin: '36px auto', padding: 16 };
const title = { margin: '10px 0 14px 0', fontSize: 22 };
const lead = {
  background: '#f7f8ff',
  border: '1px solid #e6e9ff',
  color: '#1a1f36',
  borderRadius: 10,
  padding: '10px 14px',
  marginBottom: 14,
  lineHeight: 1.6,
  whiteSpace: 'pre-line',
};
const controls = { display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 };
const row = { display: 'flex', flexWrap:'wrap', gap: 24, alignItems: 'center' };
const FIELD_DATE_LABEL_SPACING = 12;
const FIELD_DEPTH_LABEL_SPACING = -48;
const fieldDate = { display: 'flex', alignItems: 'center', fontWeight: 400, fontSize: 16.8 };
const fieldDepth = { display: 'flex', alignItems: 'center', fontWeight: 400, fontSize: 16.8 };
const fieldLabelDate = { minWidth: 165, whiteSpace: 'nowrap', display: 'inline-block', marginRight: FIELD_DATE_LABEL_SPACING };
const fieldLabelDepth = { minWidth: 165, whiteSpace: 'nowrap', display: 'inline-block', marginRight: FIELD_DEPTH_LABEL_SPACING };
const input = { borderRadius: 8, border: '1px solid #cfd6ff', padding: '8px 10px', fontSize: 15, background:'#fff' };
const select = { ...input, minWidth: 200 };
const btn = (loading) => ({
  alignSelf: 'center',
  background: loading ? '#a8b7ff' : '#4b64ff',
  border: 'none', color:'#fff',
  padding: '9px 18px', borderRadius: 8, fontWeight: 700, cursor: loading ? 'wait' : 'pointer'
});
const errorBox = {
  background: '#ffecec', border: '1px solid #f5b5b5',
  color: '#c62828', borderRadius: 8, padding: '10px 14px', fontWeight: 600, marginBottom: 12,
};

const card = { background:'#fff', border:'1px solid #eee', borderRadius: 12, padding: 12, boxShadow:'0 2px 10px #0000000d' };
const legendRow = { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 10 };
const legendLabel = { fontSize: 12, color:'#666' };
const gradientBar = {
  width: 200, height: 12, borderRadius: 999,
  background: 'linear-gradient(90deg, #e8ecff 0%, hsl(226,95%,52%) 100%)', border: '1px solid #dbe1ff'
};

const gridWrap = {
  display: 'grid',
  gridTemplateColumns: `120px repeat(24, minmax(34px, 1fr))`,
  gap: 2,
  alignItems: 'stretch',
};
const gridHeaderCell = {
  fontSize: 13, fontWeight: 700, color:'#5a67d8',
  display:'flex', alignItems:'center', justifyContent:'center'
};
const gridHour = {
  fontSize: 12, color:'#5c5f66',
  textAlign:'center', padding: '6px 0',
  background:'#f5f7ff', border:'1px solid #edf0ff', borderRadius: 6,
};
const gridWday = {
  fontWeight: 700, fontSize: 14, padding:'8px 10px',
  background:'#f0f3ff', border:'1px solid #e5e9ff', borderRadius: 8, color:'#283252'
};
const cell = {
  textAlign:'center', padding:'6px 4px',
  fontSize: 12.5, fontWeight: 700,
  borderRadius: 6, border:'1px solid #e9ecff',
  userSelect:'none',
};
